import { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { LayoutDashboard, Users2, Upload, ListTree, ShieldCheck, ScrollText, MessageSquareHeart, Lock, Menu, LogOut, Languages, FlaskConical, Flag, ClipboardList, Church, Award, FileBarChart, Star, Bot, BookOpenText } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "../ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { useAuth } from "../../lib/auth";
import { useLang, LANGS } from "../../lib/i18n";
import { api, has } from "../../lib/api";

const NAV = [
  { to: "/dashboard", key: "nav_dashboard", icon: LayoutDashboard },
  { to: "/realities", key: "nav_realities", icon: Users2, perms: ["analytics:all", "analytics:own_gkk"] },
  { to: "/priorities", key: "nav_priorities", icon: Flag, perms: ["plan:propose"] },
  { to: "/plans", key: "nav_plans", icon: ClipboardList, perms: ["plan:propose"] },
  { to: "/gkk-profiles", key: "nav_profiles", icon: Church, perms: ["analytics:all", "analytics:own_gkk"] },
  { to: "/evaluation", key: "nav_evaluation", icon: Award, perms: ["plan:propose"] },
  { to: "/reports", key: "nav_reports", icon: FileBarChart, perms: ["report:generate", "report:own_gkk", "report:briefing"] },
  { to: "/analyst", key: "nav_analyst", icon: Bot, perms: ["analyst:use"] },
  { to: "/ppc-summaries", key: "nav_ppc", icon: BookOpenText, perms: ["ppc:read"] },
  { to: "/programs", key: "nav_programs", icon: Star },
  { to: "/import", key: "nav_import", icon: Upload, perms: ["import:run"] },
  { to: "/master-list", key: "nav_master", icon: ListTree, perms: ["gkk:manage"] },
  { to: "/users", key: "nav_users", icon: ShieldCheck, perms: ["users:manage"] },
  { to: "/audit", key: "nav_audit", icon: ScrollText, perms: ["audit:view"] },
  { to: "/feedback", key: "nav_feedback", icon: MessageSquareHeart },
  { to: "/privacy", key: "nav_privacy", icon: Lock },
];

export const TauMark = ({ className = "" }) => (
  <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
    <path d="M3 4h18v3h-7.5v13h-3V7H3z" fill="currentColor" />
  </svg>
);

const NavItems = ({ user, t, onClick }) => (
  <nav className="flex flex-col gap-1">
    {NAV.filter((n) => !n.perms || has(user, ...n.perms)).map((n) => (
      <NavLink key={n.to} to={n.to} onClick={onClick} data-testid={`nav-${n.to.slice(1)}`}
        className={({ isActive }) => `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors ${isActive ? "bg-amber-900 text-amber-50" : "text-stone-700 hover:bg-amber-100/70"}`}>
        <n.icon className="h-4 w-4 shrink-0" /> {t(n.key)}
      </NavLink>
    ))}
  </nav>
);

const Brand = () => (
  <div className="flex items-center gap-3">
    <div className="grid h-10 w-10 place-items-center rounded-lg bg-amber-900 text-amber-100"><TauMark className="h-6 w-6" /></div>
    <div className="leading-tight">
      <p className="font-serif-display text-lg font-bold text-amber-950">Mabuhay</p>
      <p className="text-[10px] uppercase tracking-[0.2em] text-amber-800/80">Pastoral Intelligence</p>
    </div>
  </div>
);

export const LangSwitch = () => {
  const { lang, setLang } = useLang();
  return (
    <Select value={lang} onValueChange={setLang}>
      <SelectTrigger className="w-[130px] bg-white" data-testid="lang-switch-button"><Languages className="h-4 w-4 mr-1" /><SelectValue /></SelectTrigger>
      <SelectContent className="bg-white">
        {LANGS.map((l) => <SelectItem key={l.code} value={l.code} data-testid={`lang-option-${l.code}`}>{l.label}</SelectItem>)}
      </SelectContent>
    </Select>
  );
};

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { t } = useLang();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [sample, setSample] = useState(false);

  useEffect(() => {
    api.get("/admin/sample-status").then((r) => setSample(r.data.active)).catch(() => {});
  }, [children]);

  const signOut = async () => { await logout(); nav("/"); };

  return (
    <div className="min-h-screen bg-[#FAFAF7] overflow-x-hidden">
      {sample && (
        <div className="flex items-center gap-2 border-b border-amber-500/30 bg-amber-500/10 px-4 py-2 text-xs font-semibold text-amber-900" data-testid="test-data-banner">
          <FlaskConical className="h-4 w-4" /> {t("test_banner")}
        </div>
      )}
      <div className="flex">
        <aside className="hidden lg:flex w-64 shrink-0 flex-col gap-8 border-r border-amber-700/15 bg-[#F5F0E6] px-4 py-6 min-h-screen sticky top-0">
          <Brand />
          <NavItems user={user} t={t} />
          <p className="mt-auto text-[11px] text-stone-500 leading-relaxed">Saint Francis of Assisi Parish<br />Brgy. Mabuhay, General Santos City</p>
        </aside>
        <div className="flex-1 min-w-0">
          <header className="sticky top-0 z-30 flex items-center justify-between gap-3 border-b border-amber-700/15 bg-[#FAFAF7] px-4 sm:px-6 lg:px-10 py-3">
            <div className="flex items-center gap-2 lg:hidden">
              <Sheet open={open} onOpenChange={setOpen}>
                <SheetTrigger asChild><button className="p-2" data-testid="mobile-menu-button" aria-label="Menu"><Menu className="h-5 w-5" /></button></SheetTrigger>
                <SheetContent side="left" className="bg-[#F5F0E6] w-72">
                  <SheetTitle className="sr-only">Menu</SheetTitle>
                  <div className="mb-6"><Brand /></div>
                  <NavItems user={user} t={t} onClick={() => setOpen(false)} />
                </SheetContent>
              </Sheet>
              <Brand />
            </div>
            <p className="hidden lg:block text-sm text-stone-500 italic font-serif-display">{t("welcome")}, {user?.name?.split(" ")[0]}</p>
            <div className="flex items-center gap-3">
              <LangSwitch />
              <div className="hidden sm:block text-right leading-tight">
                <p className="text-sm font-semibold text-stone-800" data-testid="current-user-name">{user?.name}</p>
                <p className="text-[11px] text-amber-800" data-testid="current-user-role">{user?.role_label}</p>
              </div>
              <button onClick={signOut} className="rounded-lg p-2 text-stone-600 hover:bg-amber-100 transition-colors" data-testid="logout-button" aria-label={t("signout")}><LogOut className="h-4 w-4" /></button>
            </div>
          </header>
          <main className="px-4 sm:px-6 lg:px-10 py-8">{children}</main>
        </div>
      </div>
    </div>
  );
}
